import { useEffect } from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, IconButton, List } from '@mui/material';

import { submenu } from '~/types/constants/labelMenu';
import { SubMenuItem } from '~/types/constants/menu';
import roles from '~/types/constants/roles';

import { StyledListItemButton, StyledListItemText } from '../elements';

import { SubItemMenuInside } from './SubItemMenuInside';

interface SubItemMenuProps {
  subItem: SubMenuItem;
  pathname: string;
  toggleSubmenu: (label: string) => void;
  selectedSubmenu?: string | null;
  open: boolean;
  clearSelectedSubmenu: () => void;
}

export function SubItemMenu({
  subItem,
  toggleSubmenu,
  clearSelectedSubmenu,
  selectedSubmenu,
  pathname,
  open,
}: SubItemMenuProps) {
  const { data: session } = useSession();
  const role = session?.user?.role;
  const hasManufacturerId = !!session?.user?.manufacturerId;
  const isManufacturerRole =
    role === roles[3] ||
    session?.user?.projects?.some(p => p.roleName === roles[3]);
  const isManufacturer = isManufacturerRole || hasManufacturerId;

  const { href, label, disabled, subMenuItems } = subItem;
  const hasSubMenu = subMenuItems && subMenuItems.length > 0;
  const isSubmenuOpen =
    subMenuItems?.some(sub => pathname.includes(sub.href)) ||
    selectedSubmenu === label;

  useEffect(() => {
    if (pathname === '/patrocinados' && label === submenu.sponsored) {
      toggleSubmenu(label);
    } else if (
      pathname.startsWith('/campanhas-ativas') &&
      label === submenu.banners
    ) {
      toggleSubmenu(label);
    } else if (selectedSubmenu && selectedSubmenu !== label && !isSubmenuOpen) {
      clearSelectedSubmenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  if (!hasSubMenu) {
    return (
      open && (
        <SubItemMenuInside
          subItem={subItem}
          pathname={pathname}
          toggleSubmenu={toggleSubmenu}
          open={open}
        />
      )
    );
  }

  return (
    open && (
      <Box key={label}>
        <Link href={href} legacyBehavior>
          <StyledListItemButton
            className={pathname.includes(href) || isSubmenuOpen ? 'active' : ''}
            onClick={() => hasSubMenu && toggleSubmenu(label)}
            disabled={disabled}
          >
            <StyledListItemText inset>{label}</StyledListItemText>
            {hasSubMenu && (
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  toggleSubmenu(label);
                }}
                sx={{
                  marginLeft: 'auto',
                  color: 'inherit',
                  padding: 0,
                }}
              >
                {isSubmenuOpen ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </StyledListItemButton>
        </Link>
        {hasSubMenu && (
          <Collapse in={isSubmenuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {subMenuItems
                .filter(
                  item =>
                    !(item.label === submenu.performance && isManufacturer) &&
                    !(item.href === '/banners/relatorios' && isManufacturer)
                )
                .map(item => (
                  <SubItemMenuInside
                    key={item.href}
                    subItem={item}
                    pathname={pathname}
                    toggleSubmenu={toggleSubmenu}
                    open={open}
                  />
                ))}
            </List>
          </Collapse>
        )}
      </Box>
    )
  );
}
